import { action, computed, makeObservable, observable } from 'mobx';
import type { SensorAlertBundle, SensorView } from '@mark43/cad-api';
import {
    MAX_SENSOR_ALERTS,
    getSensorAlertSortTime,
    isMappableSensor,
    mergeSensorAlerts,
} from './helpers';
import type { MappableSensor } from './types';

type ArchiveWrite = { id: number; alertKeys: string[]; archived: boolean };

export class SensorsStore {
    @observable
    private data: {
        sensors: SensorView[];
        /** Oldest-first and capped — see `mergeSensorAlerts`. Read via `alerts` for display. */
        alerts: SensorAlertBundle[];
        /**
         * Keys of alerts that arrived live and were not already held, awaiting a toast.
         *
         * The queue exists because "is this a new alert or a link update?" is only answerable at
         * the moment of ingest, and the toast is raised by a component mounted below this store's
         * provider. Deciding here and queueing keeps a single writer, so the answer does not depend
         * on which stream subscriber happened to run first.
         */
        pendingToastAlertKeys: string[];
        /**
         * Keys of alerts this user has archived.
         *
         * Server-side and per user. Written optimistically on click and then reconciled onto the
         * authoritative set the archive and replay endpoints both return, so a stale client
         * converges rather than drifting. Mutations go through `useArchiveSensorAlert`.
         */
        archivedAlertKeys: string[];
        /** True when the server had more history than the replay limit asked for. */
        isAlertHistoryTruncated: boolean;
        /**
         * The server's clamp on how many alerts one replay may return, from the bootstrap bundle.
         *
         * Defaulted to the client cap so a replay before bootstrap lands still asks for something
         * sane; the server clamps to its own value regardless of what we ask for.
         */
        replayLimit: number;
    } = {
        sensors: [],
        alerts: [],
        pendingToastAlertKeys: [],
        archivedAlertKeys: [],
        isAlertHistoryTruncated: false,
        replayLimit: MAX_SENSOR_ALERTS,
    };

    /**
     * `useLayerControl` keys its entityId -> Esri objectId cache on a number, but sensor ids are
     * vendor strings. Ids are assigned on first sight and kept for the life of the store so a
     * sensor's existing graphic is updated rather than torn down and recreated on every refetch.
     *
     * Deliberately not observable: `setSensors` writes it and `mappableSensors` only reads it,
     * which keeps the computed pure.
     */
    private entityIdsBySensorId = new Map<string, number>();
    private nextEntityId = 1;

    /*
     * Archive writes the server has not answered yet, oldest first, and the chain that sends them
     * one at a time. Not observable: only the queue reads them, and what they drive —
     * `archivedAlertKeys` — already is. See `enqueueArchiveWrite`.
     */
    private pendingArchiveWrites: ArchiveWrite[] = [];
    private archiveWriteQueue: Promise<void> = Promise.resolve();
    private nextArchiveWriteId = 1;

    constructor() {
        makeObservable(this);
    }

    @computed
    get sensors(): SensorView[] {
        return this.data.sensors;
    }

    @computed
    get sensorsById(): Map<string, SensorView> {
        return new Map(this.data.sensors.map((sensor) => [sensor.externalId, sensor]));
    }

    /** Newest first, which is the order both the drawer and the camera panel display. */
    @computed
    get alerts(): SensorAlertBundle[] {
        return this.data.alerts.slice().reverse();
    }

    @computed
    get mappableSensors(): MappableSensor[] {
        const mappableSensors: MappableSensor[] = [];

        for (const sensor of this.data.sensors) {
            if (!isMappableSensor(sensor)) {
                continue;
            }
            const entityId = this.entityIdsBySensorId.get(sensor.externalId);
            if (entityId === undefined) {
                continue;
            }
            mappableSensors.push({ ...sensor, entityId });
        }

        return mappableSensors;
    }

    public getSensor(sensorId: string | undefined): SensorView | undefined {
        return sensorId === undefined ? undefined : this.sensorsById.get(sensorId);
    }

    /**
     * The camera an alert came from.
     *
     * The alert carries only `sensorExternalId` — the convergence branch dropped the camera's
     * name, coordinates, site and classification from the alert payload, so every consumer that
     * needs one of those has to come through here. Returns nothing when the directory has not
     * loaded yet or the camera has since been deactivated, which callers must render around
     * rather than assume away.
     */
    public getSensorForAlert(alert: SensorAlertBundle): SensorView | undefined {
        return this.getSensor(alert.sensorExternalId);
    }

    /** Newest first. Not a computed because it is parameterized by sensor. */
    public getAlertsForSensor(sensorId: string | undefined): SensorAlertBundle[] {
        if (sensorId === undefined) {
            return [];
        }
        return this.data.alerts
            .filter((alert) => alert.sensorExternalId === sensorId)
            .sort((a, b) => getSensorAlertSortTime(b) - getSensorAlertSortTime(a));
    }

    public getLatestAlertForSensor(sensorId: string | undefined): SensorAlertBundle | undefined {
        return this.getAlertsForSensor(sensorId)[0];
    }

    @action
    public setSensors(sensors: SensorView[]): void {
        for (const sensor of sensors) {
            if (!this.entityIdsBySensorId.has(sensor.externalId)) {
                this.entityIdsBySensorId.set(sensor.externalId, this.nextEntityId++);
            }
        }
        this.data.sensors = sensors;
    }

    /**
     * Ingests alerts, and for live ones records which were genuinely new.
     *
     * Link and unlink push the *whole updated alert*, in the same shape as a new one, so the only
     * thing that separates "new alert" from "an alert I already have was just linked" is whether
     * this client already holds the key. Held means update — no toast. Unseen means new.
     *
     * `isLive` is what keeps replayed history quiet: a cold-start or catch-up fetch ingests with
     * `isLive: false` and queues nothing, however many alerts it carries.
     *
     * Newness is measured against the current log rather than a set of every key ever seen, which
     * would grow unbounded across a 24/7 shift. The cost is a narrow false toast: an alert that
     * aged out past `MAX_SENSOR_ALERTS` and is then linked would read as new.
     */
    @action
    public upsertAlerts(alerts: SensorAlertBundle[], { isLive }: { isLive: boolean }): void {
        if (isLive) {
            const heldKeys = new Set(this.data.alerts.map((alert) => alert.alertKey));
            const newKeys = alerts
                .map((alert) => alert.alertKey)
                .filter((alertKey) => !heldKeys.has(alertKey));
            if (newKeys.length > 0) {
                this.data.pendingToastAlertKeys = [
                    ...this.data.pendingToastAlertKeys,
                    ...newKeys,
                ];
            }
        }
        this.data.alerts = mergeSensorAlerts(this.data.alerts, alerts);
    }

    /**
     * Whether this alert's key is already in the log — the same question `upsertAlerts` asks to
     * decide new-versus-update. Exposed so a caller can observe the answer before ingest, which is
     * the only moment it is still knowable.
     */
    public isAlertHeld(alert: SensorAlertBundle): boolean {
        const alertKey = alert.alertKey;
        return this.data.alerts.some((held) => held.alertKey === alertKey);
    }

    /** Alerts that arrived live and new, in arrival order, still waiting to be toasted. */
    @computed
    get alertsAwaitingToast(): SensorAlertBundle[] {
        const alertsByKey = new Map(
            this.data.alerts.map((alert) => [alert.alertKey, alert])
        );
        return this.data.pendingToastAlertKeys
            .map((alertKey) => alertsByKey.get(alertKey))
            .filter((alert): alert is SensorAlertBundle => alert !== undefined);
    }

    @action
    public clearAlertsAwaitingToast(): void {
        this.data.pendingToastAlertKeys = [];
    }

    /** The archived keys this client currently believes in. Exposed so the invariant that it
     * cannot accumulate duplicates across reconciles is observable. */
    @computed
    get archivedAlertKeys(): string[] {
        return this.data.archivedAlertKeys;
    }

    @computed
    private get archivedAlertKeySet(): Set<string> {
        return new Set(this.data.archivedAlertKeys);
    }

    public isAlertArchived(alert: SensorAlertBundle): boolean {
        return this.archivedAlertKeySet.has(alert.alertKey);
    }

    /** Newest first, like `alerts`. */
    public getAlerts({ archived }: { archived: boolean }): SensorAlertBundle[] {
        return this.alerts.filter((alert) => this.isAlertArchived(alert) === archived);
    }

    /**
     * Newest first, restricted to a set of sensors — what the grid panel's rail shows. Takes a
     * Set rather than a list because the grid repeats sensors to fill its tiles, and a repeated
     * sensor must not contribute its events twice.
     */
    public getAlertsForSensors(
        sensorIds: Set<string>,
        { archived }: { archived: boolean }
    ): SensorAlertBundle[] {
        return this.getAlerts({ archived }).filter((alert) =>
            sensorIds.has(alert.sensorExternalId)
        );
    }

    /** Optimistic single-key write. The server's set replaces it via `setArchivedAlertKeys`. */
    /**
     * Newest first, restricted to the alerts linked to one CAD event.
     *
     * `linkedEvents` comes on the alert itself, and link and unlink both push the whole updated
     * alert, so this stays correct without its own subscription — an unlink is the event id no
     * longer being present.
     */
    public getAlertsLinkedToEvent(cadEventId: number): SensorAlertBundle[] {
        return this.alerts.filter((alert) =>
            alert.linkedEvents.some((link) => link.eventId === cadEventId)
        );
    }

    /**
     * Applies a link or unlink response: `linkedAlerts` is **every alert now linked to the
     * event**, not just the ones the request named.
     *
     * That shape is why upserting the response alone was wrong for unlink. The alert just
     * unlinked is by definition absent from the response, so upserting left it in the store still
     * claiming the event — on the Sensor Events tab, the attach button and the sensor event panel
     * — until its stream push happened to land, and indefinitely if that push was missed, since a
     * catch-up replay only fetches alerts newer than the newest one held.
     *
     * So the response is authoritative for this one event: anything it carries is linked, and
     * anything held that claims the event but is absent from it is not. Other events' links are
     * untouched. Also corrects a client whose view drifted, which is what the whole-set shape is for.
     */
    @action
    public reconcileEventLinks(cadEventId: number, linkedAlerts: SensorAlertBundle[]): void {
        const linkedKeys = new Set(linkedAlerts.map((alert) => alert.alertKey));
        const noLongerLinked = this.data.alerts
            .filter(
                (alert) =>
                    !linkedKeys.has(alert.alertKey) &&
                    alert.linkedEvents.some((link) => link.eventId === cadEventId)
            )
            .map((alert) => ({
                ...alert,
                linkedEvents: alert.linkedEvents.filter((link) => link.eventId !== cadEventId),
            }));
        this.upsertAlerts([...noLongerLinked, ...linkedAlerts], { isLive: false });
    }

    @action
    public setAlertArchived(alertKey: string, isArchived: boolean): void {
        const withoutKey = this.data.archivedAlertKeys.filter((key) => key !== alertKey);
        this.data.archivedAlertKeys = isArchived ? [...withoutKey, alertKey] : withoutKey;
    }

    /**
     * Replaces the archived set wholesale with the server's.
     *
     * Only `/alerts/archive` may call this. Its response is the acting user's **entire** archive
     * within the retention window — deliberately not narrowed to the keys in the request — which
     * is what makes that endpoint self-correcting for a client whose view drifted.
     */
    @action
    public setArchivedAlertKeys(archivedAlertKeys: string[]): void {
        this.data.archivedAlertKeys = archivedAlertKeys;
    }

    /**
     * Applies a **page-scoped** archived set: the server's answer for the alerts in one response,
     * and only those.
     *
     * `/sensors/bootstrap` and `/sensors/alerts/recent` both narrow `archivedAlertKeys` to keys
     * belonging to alerts they actually returned, because the page is clamped by the replay limit
     * while a user's archive grows with every alert they have ever dismissed. Treating one of
     * those sets as the whole archive would un-archive everything it did not mention: a reconnect
     * replay carrying two new alerts would wipe every archived row on screen.
     *
     * So the response is authoritative for the keys it covers and silent about the rest — drop
     * our belief for the alerts in this response, then take the server's.
     */
    @action
    public reconcileArchivedAlertKeys(
        alertsInResponse: SensorAlertBundle[],
        archivedAlertKeys: string[]
    ): void {
        const keysInResponse = new Set(alertsInResponse.map((alert) => alert.alertKey));
        const untouched = this.data.archivedAlertKeys.filter((key) => !keysInResponse.has(key));
        this.data.archivedAlertKeys = [...new Set([...untouched, ...archivedAlertKeys])];
        // A replay landing mid-write reports the state from before the write.
        this.reapplyPendingArchiveWrites();
    }

    /**
     * Archives or restores, optimistically, then sends the write once every earlier one has
     * settled.
     *
     * Serialized because `/alerts/archive` answers with the user's **whole** archived set, which
     * is applied wholesale. Two writes in flight at once can be answered in either order, and a
     * stale answer landing last un-archives whatever the other one wrote. "Archive all" used to
     * fire a request per alert and hit exactly this: rows archived on the click reappeared a
     * moment later. One at a time, the last answer is always the newest.
     *
     * Writes still queued behind the one that settled are re-applied over its answer, because the
     * server has not seen them yet. A failed write rolls back its own keys only; the API client
     * has already raised the error toast.
     *
     * `send` performs the request and resolves to the server's archived set.
     */
    public enqueueArchiveWrite(
        alertKeys: string[],
        archived: boolean,
        send: () => Promise<string[] | undefined>
    ): Promise<void> {
        const write: ArchiveWrite = { id: this.nextArchiveWriteId++, alertKeys, archived };
        this.pendingArchiveWrites.push(write);
        this.applyArchiveIntent(alertKeys, archived);

        const sendWrite = async () => {
            let confirmedKeys: string[] | undefined;
            let failed = false;
            try {
                confirmedKeys = await send();
            } catch {
                failed = true;
            }
            this.settleArchiveWrite(write, { confirmedKeys, failed });
        };
        // `sendWrite` never rejects, so one failure cannot stall every write queued after it.
        this.archiveWriteQueue = this.archiveWriteQueue.then(sendWrite);
        return this.archiveWriteQueue;
    }

    @action
    private applyArchiveIntent(alertKeys: string[], archived: boolean): void {
        const keys = new Set(alertKeys);
        const withoutKeys = this.data.archivedAlertKeys.filter((key) => !keys.has(key));
        this.data.archivedAlertKeys = archived ? [...withoutKeys, ...keys] : withoutKeys;
    }

    @action
    private settleArchiveWrite(
        write: ArchiveWrite,
        { confirmedKeys, failed }: { confirmedKeys: string[] | undefined; failed: boolean }
    ): void {
        this.pendingArchiveWrites = this.pendingArchiveWrites.filter(({ id }) => id !== write.id);
        if (failed) {
            // Callers only ever write the opposite of the current state, so that is what to restore.
            this.applyArchiveIntent(write.alertKeys, !write.archived);
        } else if (confirmedKeys) {
            this.data.archivedAlertKeys = confirmedKeys;
        }
        this.reapplyPendingArchiveWrites();
    }

    @action
    private reapplyPendingArchiveWrites(): void {
        for (const pending of this.pendingArchiveWrites) {
            this.applyArchiveIntent(pending.alertKeys, pending.archived);
        }
    }

    /** The server's replay clamp, or the client cap until the bootstrap bundle has landed. */
    @computed
    get replayLimit(): number {
        return this.data.replayLimit;
    }

    @action
    public setReplayLimit(replayLimit: number): void {
        if (replayLimit > 0) {
            this.data.replayLimit = replayLimit;
        }
    }

    /**
     * Whether the server had more history than we asked for. Surfaced rather than swallowed: a
     * list that silently stops short reads as "that is all there was".
     */
    @computed
    get isAlertHistoryTruncated(): boolean {
        return this.data.isAlertHistoryTruncated;
    }

    @action
    public setAlertHistoryTruncated(isTruncated: boolean): void {
        this.data.isAlertHistoryTruncated = isTruncated;
    }
}
